import { useState, useEffect } from 'react';
import { useTheme } from '@/context/ThemeContext';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === 'dark';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const navBg = scrolled
    ? isDark
      ? 'bg-black/90 backdrop-blur-xl border-b border-white/8'
      : 'bg-gray-900/92 backdrop-blur-xl border-b border-white/10'
    : 'bg-transparent';

  const textColor = scrolled
    ? isDark ? 'text-white/70 hover:text-white' : 'text-white/70 hover:text-white'
    : 'text-white/80 hover:text-white';

  const logoColor = scrolled
    ? isDark ? 'text-white' : 'text-white'
    : 'text-white';

  return (
    <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-16 md:h-20">
        <button onClick={() => scrollTo('hero')} className="cursor-pointer flex items-center" aria-label="AI Prime Systems Inc">
          <img
            src="https://public.readdy.ai/ai/img_res/20b58411-1e66-4796-a013-aba2afbad94e.png"
            alt="AI Prime Systems Inc Logo"
            className="h-10 md:h-12 w-auto object-contain"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'block';
            }}
          />
          <span className={`hidden font-sora font-700 text-lg md:text-xl tracking-tight ${logoColor} transition-colors duration-300`}>
            AI Prime{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF]">
              Systems
            </span>
          </span>
        </button>

        <div className="hidden md:flex items-center gap-8">
          {[
            { label: 'Home', id: 'hero' },
            { label: 'Industries', id: 'industries' },
            { label: 'Results', id: 'case-studies' },
            { label: 'Contact', id: 'contact' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className={`text-sm font-grotesk transition-colors cursor-pointer whitespace-nowrap ${textColor}`}
            >
              {item.label}
            </button>
          ))}

          <button
            onClick={toggleTheme}
            aria-label="Toggle day/night mode"
            className="relative flex items-center w-14 h-7 rounded-full border transition-all duration-300 cursor-pointer border-white/20 bg-white/10 hover:border-white/35 flex-shrink-0"
          >
            <span
              className={`absolute top-0.5 w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 ${
                isDark
                  ? 'left-0.5 bg-[#1a1a2e]'
                  : 'left-[calc(100%-1.625rem)] bg-amber-400'
              }`}
            >
              <i className={`text-xs ${isDark ? 'ri-moon-fill text-[#4FA3FF]' : 'ri-sun-fill text-white'}`}></i>
            </span>
          </button>

          <button
            onClick={() => scrollTo('contact')}
            className="px-5 py-2 rounded-full text-sm font-grotesk font-600 bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF] text-black hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
          >
            Book a Demo
          </button>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <button
            onClick={toggleTheme}
            aria-label="Toggle day/night mode"
            className="relative flex items-center w-12 h-6 rounded-full border transition-all duration-300 cursor-pointer border-white/20 bg-white/10"
          >
            <span
              className={`absolute top-0.5 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 ${
                isDark
                  ? 'left-0.5 bg-[#1a1a2e]'
                  : 'left-[calc(100%-1.375rem)] bg-amber-400'
              }`}
            >
              <i className={`text-[10px] ${isDark ? 'ri-moon-fill text-[#4FA3FF]' : 'ri-sun-fill text-white'}`}></i>
            </span>
          </button>
          <button
            className="w-8 h-8 flex items-center justify-center cursor-pointer text-white"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t px-6 py-5 flex flex-col gap-4 bg-gray-950/98 border-white/5">
          {[
            { label: 'Home', id: 'hero' },
            { label: 'Industries', id: 'industries' },
            { label: 'Results', id: 'case-studies' },
            { label: 'Contact', id: 'contact' },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-left text-sm font-grotesk transition-colors cursor-pointer text-white/70 hover:text-white"
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={() => scrollTo('contact')}
            className="px-5 py-2.5 rounded-full text-sm font-grotesk font-600 bg-gradient-to-r from-[#38D6D6] to-[#4FA3FF] text-black cursor-pointer whitespace-nowrap text-center"
          >
            Book a Demo
          </button>
        </div>
      )}
    </nav>
  );
}
